export type ClientEvent =
  | { type: 'session.start'; occurredAt: number; metadata?: Record<string, unknown> }
  | { type: 'session.heartbeat'; occurredAt: number; visibleMs: number }
  | { type: 'session.end'; occurredAt: number; visibleMs: number; reason: string }
  | { type: 'api_call'; occurredAt: number; provider: string; model: string;
      inputTokens?: number; outputTokens?: number; costUsd: number | null;
      metadata?: Record<string, unknown> };

export interface QueueOptions {
  url: string;
  getToken: () => string | null;
  flushIntervalMs: number;
  batchMax: number;
  capacity?: number;
  backoffMaxMs?: number;
}

export interface UsageQueue {
  enqueue(event: ClientEvent): void;
  flush(): Promise<void>;
  flushBeacon(): void;
  size(): number;
  snapshot(): ClientEvent[];
  stop(): void;
}

export function createUsageQueue(opts: QueueOptions): UsageQueue {
  const capacity = opts.capacity ?? 200;
  const backoffMaxMs = opts.backoffMaxMs ?? 5 * 60_000;
  const queue: ClientEvent[] = [];
  // Number of intervals to skip before retrying after a failed flush.
  // Uses interval-count-based backoff to avoid Date.now() drift with fake timers.
  let skipIntervals = 0;
  let backoffMs = opts.flushIntervalMs;
  let stopped = false;
  // Guard against concurrent flushes (e.g. size-based + timer-based overlap).
  let flushing = false;

  function dropOldestPreservingEnd() {
    // Find oldest non-session.end event and drop it.
    for (let i = 0; i < queue.length; i++) {
      if (queue[i].type !== 'session.end') {
        queue.splice(i, 1);
        return;
      }
    }
    // All session.end? Drop the oldest one to make room.
    queue.shift();
  }

  function enqueue(event: ClientEvent) {
    if (queue.length >= capacity) dropOldestPreservingEnd();
    queue.push(event);
    if (queue.length >= opts.batchMax) {
      // Trigger immediate flush async (don't block caller).
      void flushInternal();
    }
  }

  async function flushInternal() {
    if (stopped || queue.length === 0) return;
    if (skipIntervals > 0) { skipIntervals--; return; }
    if (flushing) return;
    flushing = true;

    // Build batch: take up to batchMax non-session.end events, preserving session.end in queue.
    // session.end events must survive until explicitly flushed via flushBeacon or a dedicated call.
    const sessionEnds: ClientEvent[] = [];
    const regular: ClientEvent[] = [];
    for (const e of queue) {
      if (e.type === 'session.end') sessionEnds.push(e);
      else regular.push(e);
    }
    const batch = regular.slice(0, opts.batchMax);
    if (batch.length === 0) {
      flushing = false;
      return;
    }
    // Rebuild queue: remaining regular events + all session.end events at the end.
    const remaining = regular.slice(opts.batchMax);
    queue.length = 0;
    queue.push(...remaining, ...sessionEnds);

    const token = opts.getToken();
    try {
      const res = await fetch(opts.url, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ events: batch }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      // success — reset backoff
      backoffMs = opts.flushIntervalMs;
      skipIntervals = 0;
    } catch (err) {
      // Put events back at the front and back off.
      queue.unshift(...batch);
      backoffMs = Math.min(backoffMs * 2, backoffMaxMs);
      // Skip the appropriate number of intervals before retrying.
      // e.g. backoffMs=60s, flushIntervalMs=30s → skip 1 interval, retry at 2nd.
      skipIntervals = Math.max(1, Math.ceil(backoffMs / opts.flushIntervalMs) - 1);
      console.warn('[sidekick usage] flush failed, retry in', backoffMs, 'ms', err);
    } finally {
      flushing = false;
    }
  }

  function flushBeacon() {
    if (queue.length === 0) return;
    const token = opts.getToken();
    const url = token ? `${opts.url}?token=${encodeURIComponent(token)}` : opts.url;
    const body = JSON.stringify({ events: queue.splice(0, queue.length) });
    try { (globalThis as any).navigator?.sendBeacon?.(url, body); }
    catch (err) { console.warn('[sidekick usage] sendBeacon failed', err); }
  }

  const timer = setInterval(() => { void flushInternal(); }, opts.flushIntervalMs);

  return {
    enqueue,
    flush: flushInternal,
    flushBeacon,
    size() { return queue.length; },
    snapshot() { return queue.slice(); },
    stop() { stopped = true; clearInterval(timer); },
  };
}
